import * as Blockly from 'blockly/core'
import { LuaGenerator, Order } from 'blockly/lua'
import {
  BlockDefinition,
  numberInput,
  numberOutput,
  statementBlock,
} from './common'
import { TurnLeft, TurnRight } from './icons'

export const movementDefaults: Partial<BlockDefinition> = {
  style: 'movement',
}

export const movementBlocks: Array<BlockDefinition> = [
  {
    opcode: 'movement_setCanCollide',
    ...statementBlock,
    generator: () => 'script.Parent.CanCollide = true\n',
    message0: '%{BKY_B_SET_CAN_COLLIDE}',
  },
  {
    opcode: 'movement_unsetCanCollide',
    ...statementBlock,
    generator: () => 'script.Parent.CanCollide = false\n',
    message0: '%{BKY_B_UNSET_CAN_COLLIDE}',
  },
  {
    opcode: 'movement_setAnchored',
    ...statementBlock,
    generator: () => 'script.Parent.Anchored = true\n',
    message0: '%{BKY_B_SET_ANCHORED}',
  },
  {
    opcode: 'movement_unsetAnchored',
    ...statementBlock,
    generator: () => 'script.Parent.Anchored = false\n',
    message0: '%{BKY_B_UNSET_ANCHORED}',
  },
  {
    opcode: 'movement_moveSteps',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = generator.valueToCode(block, 'VAL', Order.NONE)
      return `script.Parent.Position += Vector3.new(${n},0,0)\n`
    },
    message0: '%{BKY_B_MOVE_STEPS}',
  },
  {
    opcode: 'movement_setXYZPositionTo',
    ...statementBlock,
    args0: [
      { type: 'input_value', name: 'X', check: 'Number' },
      { type: 'input_value', name: 'Y', check: 'Number' },
      { type: 'input_value', name: 'Z', check: 'Number' },
    ],
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const x = generator.valueToCode(block, 'X', Order.NONE)
      const y = generator.valueToCode(block, 'Y', Order.NONE)
      const z = generator.valueToCode(block, 'Z', Order.NONE)
      return `script.Parent.Position = Vector3.new(${x},${y},${z})\n`
    },
    message0: '%{BKY_B_SET_XYZ_POSITION_TO}',
  },
  {
    opcode: 'movement_setXTo',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = generator.valueToCode(block, 'VAL', Order.NONE)
      return `script.Parent.Position = Vector3.new(${n},script.Parent.Position.Y,script.Parent.Position.Z)\n`
    },
    message0: '%{BKY_B_SET_X_TO}',
  },
  {
    opcode: 'movement_setYTo',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = generator.valueToCode(block, 'VAL', Order.NONE)
      return `script.Parent.Position = Vector3.new(script.Parent.Position.X,${n},script.Parent.Position.Z)\n`
    },
    message0: '%{BKY_B_SET_Y_TO}',
  },
  {
    opcode: 'movement_setZTo',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = generator.valueToCode(block, 'VAL', Order.NONE)
      return `script.Parent.Position = Vector3.new(script.Parent.Position.X,script.Parent.Position.Y,${n})\n`
    },
    message0: '%{BKY_B_SET_Z_TO}',
  },
  {
    opcode: 'movement_xPosition',
    ...numberOutput,
    generator: () => ['script.Parent.Position.X', Order.ATOMIC],
    message0: '%{BKY_B_X_POSITION}',
  },
  {
    opcode: 'movement_yPosition',
    ...numberOutput,
    generator: () => ['script.Parent.Position.Y', Order.ATOMIC],
    message0: '%{BKY_B_Y_POSITION}',
  },
  {
    opcode: 'movement_zPosition',
    ...numberOutput,
    generator: () => ['script.Parent.Position.Z', Order.ATOMIC],
    message0: '%{BKY_B_Z_POSITION}',
  },
  {
    opcode: 'movement_changeXPositionBy',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = generator.valueToCode(block, 'VAL', Order.NONE)
      return `script.Parent.Position += Vector3.new(${n},0,0)\n`
    },
    message0: '%{BKY_B_CHANGE_X_POSITION_BY}',
  },
  {
    opcode: 'movement_changeYPositionBy',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = generator.valueToCode(block, 'VAL', Order.NONE)
      return `script.Parent.Position += Vector3.new(0,${n},0)\n`
    },
    message0: '%{BKY_B_CHANGE_Y_POSITION_BY}',
  },
  {
    opcode: 'movement_changeZPositionBy',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = generator.valueToCode(block, 'VAL', Order.NONE)
      return `script.Parent.Position += Vector3.new(0,0,${n})\n`
    },
    message0: '%{BKY_B_CHANGE_Z_POSITION_BY}',
  },
  {
    opcode: 'movement_setYOrientationTo',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = generator.valueToCode(block, 'VAL', Order.NONE)
      return `script.Parent.Orientation = Vector3.new(0,${n},0)\n`
    },
    message0: '%{BKY_B_SET_Y_ORIENTATION_TO}',
  },
  {
    opcode: 'movement_turnRightBy',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = generator.valueToCode(block, 'VAL', Order.NONE)
      return `script.Parent.CFrame *= CFrame.Angles(0,math.rad(${n}),0)\n`
    },
    icon: TurnRight,
    message0: '%{BKY_B_TURN_RIGHT_BY}',
  },
  {
    opcode: 'movement_turnLeftBy',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = generator.valueToCode(block, 'VAL', Order.UNARY)
      return `script.Parent.CFrame *= CFrame.Angles(0,math.rad(-${n}),0)\n`
    },
    icon: TurnLeft,
    message0: '%{BKY_B_TURN_LEFT_BY}',
  },
  {
    opcode: 'movement_changeXOrientationBy',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = generator.valueToCode(block, 'VAL', Order.NONE)
      return `script.Parent.CFrame *= CFrame.Angles(math.rad(${n}),0,0)\n`
    },
    message0: '%{BKY_B_CHANGE_X_ORIENTATION_BY}',
  },
  {
    opcode: 'movement_changeYOrientationBy',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = generator.valueToCode(block, 'VAL', Order.NONE)
      return `script.Parent.CFrame *= CFrame.Angles(0,math.rad(${n}),0)\n`
    },
    message0: '%{BKY_B_CHANGE_Y_ORIENTATION_BY}',
  },
  {
    opcode: 'movement_changeZOrientationBy',
    ...statementBlock,
    ...numberInput,
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const n = generator.valueToCode(block, 'VAL', Order.NONE)
      return `script.Parent.CFrame *= CFrame.Angles(0,0,math.rad(${n}))\n`
    },
    message0: '%{BKY_B_CHANGE_Z_ORIENTATION_BY}',
  },
  {
    opcode: 'movement_setXYZOrientationTo',
    ...statementBlock,
    args0: [
      { type: 'input_value', name: 'X', check: 'Number' },
      { type: 'input_value', name: 'Y', check: 'Number' },
      { type: 'input_value', name: 'Z', check: 'Number' },
    ],
    generator: (block: Blockly.Block, generator: LuaGenerator) => {
      const x = generator.valueToCode(block, 'X', Order.NONE)
      const y = generator.valueToCode(block, 'Y', Order.NONE)
      const z = generator.valueToCode(block, 'Z', Order.NONE)
      return `script.Parent.Orientation = Vector3.new(${x},${y},${z})\n`
    },
    message0: '%{BKY_B_SET_XYZ_ORIENTATION_TO}',
  },
  {
    opcode: 'movement_xOrientation',
    ...numberOutput,
    generator: () => ['script.Parent.Orientation.X', Order.ATOMIC],
    message0: '%{BKY_B_X_ORIENTATION}',
  },
  {
    opcode: 'movement_yOrientation',
    ...numberOutput,
    generator: () => ['script.Parent.Orientation.Y', Order.ATOMIC],
    message0: '%{BKY_B_Y_ORIENTATION}',
  },
  {
    opcode: 'movement_zOrientation',
    ...numberOutput,
    generator: () => ['script.Parent.Orientation.Z', Order.ATOMIC],
    message0: '%{BKY_B_Z_ORIENTATION}',
  },
]
